/**
 * V3.4 (2026-05-29) — chiefeco-prototype-v2 Icon component.
 *
 * Renders one symbol from <IconSprite />. Size flows from font-size
 * (the inner svg is 1em × 1em), color from currentColor — so the
 * icon picks up whatever Tailwind text-{color} / text-{size} the
 * surrounding element has. Set `size` and `color` props for one-off
 * overrides.
 *
 * Usage:
 *   <Icon name="i-microphone" />            // 1em, currentColor
 *   <Icon name="i-check" size={20} />       // 20px square
 *   <Icon name="i-bolt" color="var(--gold-3)" />
 *   <span className="text-gold-3 text-lg"><Icon name="i-quote" /></span>
 */

import type { CSSProperties, JSX } from 'react';
import type { IconName } from './IconSprite';

type Props = {
  name: IconName;
  /** Pixel size. Default uses inherited font-size. */
  size?: number;
  /** Overrides currentColor. */
  color?: string;
  className?: string;
  'aria-label'?: string;
};

export function Icon({
  name,
  size,
  color,
  className,
  'aria-label': ariaLabel,
}: Props): JSX.Element {
  const style: CSSProperties = {
    width: '1em',
    height: '1em',
    display: 'inline-block',
    verticalAlign: '-0.125em',
    flexShrink: 0,
    ...(size != null ? { fontSize: `${size}px` } : {}),
    ...(color != null ? { color } : {}),
  };
  return (
    <svg
      role={ariaLabel ? 'img' : 'presentation'}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      className={className}
      style={style}
    >
      <use href={`#${name}`} />
    </svg>
  );
}
